import dispatcher from '../../frontend/dispatcher';
import { LogError } from '../../utils/ErrorTelemetry';
import { ErrorStringFromException, PromiseWithRetry, wait } from '../../utils/utils';
import { CreateDocumentStorage } from '../factory/DocumentStorageFactory';
import { TranscriptionStorageType } from '../interfaces/IStoredTranscription';
import { LogEventNames } from '../interfaces/ITelemetryLogger';
import { DocumentStorage } from '../office-js/DocumentStorage';
import { HostAppSettingName, HostAppSettings } from '../office-js/HostAppSettings';

export enum StorageMigrationStatus {
  MigrationNotRequired,
  MigrationInitiated,
  MigrationWriteToNewStorageFailed,
  MigrationDeleteFromOldStorageFailed,
  MigrationFinished,
}

export const StorageMigrationActionTypes = {
  StorageMigrationProgress: 'STORAGE_MIGRATION_INPROGRESS',
  StorageMigrationFinished: 'STORAGE_MIGRATION_FINISHED',
};

let MIGRATION_STATE: StorageMigrationStatus = StorageMigrationStatus.MigrationNotRequired;
export let storageMigrationTaskPromise: Promise<StorageMigrationStatus> | null = null;

export function GetStorageMigrationState() {
  return MIGRATION_STATE;
}

export function UpdateStorageMigrationState(storageMigrationStatus: StorageMigrationStatus) {
  MIGRATION_STATE = storageMigrationStatus;
}

const RETRY_ATTEMPTS = 3;
const RETRY_DELAY_INTERVAL_MSEC = 1000;

export class StorageMigrationHandler {
  private newDocumentStorage: DocumentStorage;
  private oldDocumentStorageType: TranscriptionStorageType;

  constructor(newDocumentStorage: DocumentStorage, oldDocumentStorageType: TranscriptionStorageType) {
    this.newDocumentStorage = newDocumentStorage;
    this.oldDocumentStorageType = oldDocumentStorageType;
  }

  async HandleStorageMigration(storedTranscription: string): Promise<StorageMigrationStatus> {
    if (this.newDocumentStorage.SupportedDocumentStorageType == this.oldDocumentStorageType) {
      return Promise.resolve(MIGRATION_STATE);
    }

    MIGRATION_STATE = StorageMigrationStatus.MigrationInitiated;

    dispatcher.dispatch({
      type: StorageMigrationActionTypes.StorageMigrationProgress,
    });

    storageMigrationTaskPromise = new Promise<StorageMigrationStatus>((resolve, _) => {

      //copy content from oldStorage to current storage.
      PromiseWithRetry<void>(() => {
        return this.newDocumentStorage.WriteTranscribeContentToClient(storedTranscription)
      }, RETRY_ATTEMPTS, RETRY_DELAY_INTERVAL_MSEC).then(async () => {

          // update HostAppSetting for StoredTranscription.
          HostAppSettings.Set(HostAppSettingName.StoredTranscription, storedTranscription);

          //wait to showcase message to user on loading screen.
          await wait(2000);

          const oldDocumentStorage = CreateDocumentStorage(this.oldDocumentStorageType);

          //Delete Transcription Content from Old DocumentStorage, will fail after 3 retry attempts.
          PromiseWithRetry(() => {return oldDocumentStorage.DeleteTranscribeContentFromClient()}, RETRY_ATTEMPTS, RETRY_DELAY_INTERVAL_MSEC /*delayInterval*/)
            .then(() => {
              //Mark Migration Status as Finished on successful write to new storage and deletion from old storage.
              MIGRATION_STATE = StorageMigrationStatus.MigrationFinished;

              dispatcher.dispatch({
                type: StorageMigrationActionTypes.StorageMigrationFinished,
              });

              return resolve(MIGRATION_STATE);
            })
            .catch((e) => {

              //Delete from old storage failed: Mark Migration Status as Failed.
              MIGRATION_STATE = StorageMigrationStatus.MigrationDeleteFromOldStorageFailed;
              LogError(
                LogEventNames.Warning,
                'Transcription content delete operation from old storage (' +
                  oldDocumentStorage.SupportedDocumentStorageType +
                  ') failed with error ' +
                  ErrorStringFromException(e)
              );

              dispatcher.dispatch({
                type: StorageMigrationActionTypes.StorageMigrationFinished,
              });

              return resolve(MIGRATION_STATE);
            });
        })
        .catch((e) => {

          //Write to new storage failed: Mark Migration Status as Failed.
          MIGRATION_STATE = StorageMigrationStatus.MigrationWriteToNewStorageFailed;

          LogError(
            LogEventNames.Warning,
            'Transcription content write operation to storage (' +
              this.newDocumentStorage.SupportedDocumentStorageType +
              ') failed with error ' +
              ErrorStringFromException(e)
          );

          return resolve(MIGRATION_STATE);
        });
    });
    return storageMigrationTaskPromise;
  }
}
